<script setup lang="ts">
import type { Subject } from '@/components/subject/configs';
import { ArchiveX, ArrowRight, Bell, BookOpen, ChevronLeft, ChevronRight, Search, X } from 'lucide-vue-next';
import { computed, nextTick, ref, watch } from 'vue';
import { clearDynamicCourseContext, saveDynamicCourseContext } from '@/components/course/courseUtils';
import CourseIllustration from '@/components/home/CourseIllustration.vue';
import AvatarButton from '@/components/navbar/AvatarButton.vue';
import { useConfigStore, useUserStore } from '@/store';
import { isAnnouncementManagerUser, isVisitor } from '@/utils/auth';
import { openImathbook } from '@/utils/imathbookSso';

const props = defineProps<{ subjects: Subject[]; manageableSubjectIds: string[] }>();
const emit = defineEmits<{ manageSubject: [subject: Subject] }>();
const configStore = useConfigStore();
const userStore = useUserStore();
const canManageAnnouncements = computed(() => isAnnouncementManagerUser(userStore.user));
const query = ref('');
const page = ref(0);
const courseSection = ref<HTMLElement>();
const guideUrl = 'https://ecng9aaaudb2.feishu.cn/wiki/LKLiwqUBciCZK7kqwZrcEOn5nJf?from=from_copylink';
const filteredSubjects = computed(() => {
  const term = query.value.trim().toLocaleLowerCase();
  return props.subjects.filter(subject => `${subject.name} ${subject.description}`.toLocaleLowerCase().includes(term));
});
const totalPages = computed(() => Math.max(1, Math.ceil(filteredSubjects.value.length / 8)));
const visibleSubjects = computed(() => filteredSubjects.value.slice(page.value * 8, page.value * 8 + 8));
watch(query, () => {
  page.value = 0;
});
watch(totalPages, (count) => {
  page.value = Math.min(page.value, count - 1);
});
function showCourses() {
  courseSection.value?.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
}
function selectSubject(subject: Subject) {
  localStorage.setItem('cube-current-index', String(props.subjects.findIndex(item => item.id === subject.id)));
  if ('isCustomCourse' in subject && subject.isCustomCourse) {
    configStore.currentSubjectId = null;
    const dynamicSubject = { ...subject, isCustomCourse: true, showWeeklyThought: false };
    configStore.dynamicSubject = dynamicSubject;
    saveDynamicCourseContext(dynamicSubject, '/');
    void resetShellScroll();
    return;
  }
  configStore.dynamicSubject = null;
  clearDynamicCourseContext();
  configStore.currentSubjectId = subject.id;
  void resetShellScroll();
}
function resetShellScroll() {
  return nextTick(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
    document.querySelectorAll<HTMLElement>('[data-course-scroll], .home-shell, .page-shell, main').forEach((element) => {
      if (element.scrollHeight > element.clientHeight)
        element.scrollTop = 0;
    });
  });
}
function courseName(subject: Subject) {
  return subject.id === 'linear-algebra-en' ? '双语线性代数 / Linear' : subject.name;
}
</script>

<template>
  <div class="home-landing">
    <header class="home-header">
      <a class="brand" href="/" aria-label="邮趣数学首页" @click.prevent="query = ''; page = 0">
        <img class="brand-logo" src="/umath-logo.png" alt="UMath 邮趣数学">
      </a>
      <nav class="home-nav" aria-label="主导航">
        <a href="/" class="active" aria-current="page" @click.prevent="query = ''; page = 0">首页</a>
        <RouterLink to="/announcements">
          公告
        </RouterLink>
        <RouterLink v-if="canManageAnnouncements" to="/admin/announcements">
          管理公告
        </RouterLink>
        <RouterLink to="/exam-analysis">
          试卷分析
        </RouterLink>
        <RouterLink to="/ai">
          大模型问答
        </RouterLink>
        <RouterLink to="/learning-tools">
          学习工具
        </RouterLink>
        <a class="imathbook-link" href="https://imathbook.com/bookshelf" @click.prevent="openImathbook">
          <BookOpen :size="16" aria-hidden="true" />iMathBook
        </a>
        <a :href="guideUrl" target="_blank" rel="noopener noreferrer">用户手册</a>
      </nav>
      <div class="header-actions">
        <form class="course-search" role="search" @submit.prevent="showCourses">
          <Search :size="20" aria-hidden="true" />
          <input v-model="query" aria-label="搜索课程" placeholder="搜索你感兴趣的课程…" @input="showCourses">
          <button v-if="query" type="button" aria-label="清空搜索" @click="query = ''">
            <X :size="16" />
          </button>
        </form>
        <RouterLink class="notification-button" to="/notifications" aria-label="消息通知">
          <Bell :size="21" />
        </RouterLink>
        <AvatarButton v-if="!isVisitor()" class="home-avatar" />
        <RouterLink v-else class="login-link" to="/login">
          登录
        </RouterLink>
      </div>
    </header>

    <main class="home-content">
      <section class="home-hero" aria-labelledby="home-title">
        <div class="hero-campus" aria-hidden="true" />
        <div class="hero-copy">
          <p class="university-name">
            BEIJING UNIVERSITY OF POSTS AND TELECOMMUNICATIONS
          </p>
          <h1 id="home-title">
            让数学学习<br>更高效，更有趣
          </h1>
          <p class="hero-description">
            <span>Learn smart, grow smarter</span><span>Intelligent learning. Tailored for you.</span>
          </p>
          <a class="guide-button" :href="guideUrl" target="_blank" rel="noopener noreferrer"><BookOpen :size="23" />使用手册</a>
        </div>
      </section>

      <section id="home-courses" ref="courseSection" class="course-section" aria-labelledby="courses-title">
        <div class="section-heading">
          <div class="section-title">
            <h2 id="courses-title">
              课程学习
            </h2><p>选择你感兴趣的课程，开启高效学习之旅</p>
          </div>
          <div class="section-controls">
            <button aria-label="上一页课程" :disabled="page === 0" @click="page--">
              <ChevronLeft :size="21" />
            </button>
            <button aria-label="下一页课程" :disabled="page >= totalPages - 1" @click="page++">
              <ChevronRight :size="21" />
            </button>
          </div>
        </div>
        <div v-if="visibleSubjects.length" class="course-grid">
          <article v-for="(subject, index) in visibleSubjects" :key="subject.id" class="course-card">
            <button class="course-entry" :aria-label="`进入${subject.name}课程`" @click="selectSubject(subject)">
              <span class="course-number">{{ String(page * 8 + index + 1).padStart(2, '0') }}</span>
              <span class="course-icon"><component :is="subject.icon" :size="22" :stroke-width="1.7" /></span>
              <h3>{{ courseName(subject) }}</h3>
              <p>{{ subject.description }}</p>
              <CourseIllustration class="course-illustration" :subject-id="subject.id" />
              <span class="course-arrow"><ArrowRight :size="19" /></span>
            </button>
            <button v-if="manageableSubjectIds.includes(String(subject.id))" class="manage-course" :aria-label="`下架${subject.name}`" @click="emit('manageSubject', subject)">
              <ArchiveX :size="17" />
            </button>
          </article>
        </div>
        <div v-else class="empty-courses" role="status">
          <Search :size="30" /><h3>没有找到相关课程</h3><p>试试“高等数学”“概率”或“线性代数”</p><button @click="query = ''">
            查看全部课程
          </button>
        </div>
        <nav v-if="totalPages > 1" class="pagination" aria-label="课程分页">
          <button :disabled="page === 0" @click="page--">
            上一页
          </button>
          <button v-for="n in totalPages" :key="n" :class="{ selected: page === n - 1 }" :aria-current="page === n - 1 ? 'page' : undefined" :aria-label="`第${n}页`" @click="page = n - 1">
            {{ n }}
          </button>
          <button :disabled="page >= totalPages - 1" @click="page++">
            下一页
          </button>
        </nav>
        <div class="other-courses">
          <RouterLink to="/high-school">
            高中数学 <ArrowRight :size="14" />
          </RouterLink>
        </div>
      </section>
    </main>
  </div>
</template>

<style scoped>
.home-landing {
  --ink: #0a153a;
  --muted: #7183ad;
  --blue: #1c61ff;
  height: 100%;
  display: flex;
  flex-direction: column;
  color: var(--ink);
  background: #f8fbff;
  font-family: 'NotoSansSC', 'Microsoft YaHei', sans-serif;
}
.home-header {
  min-height: 74px;
  display: flex;
  align-items: center;
  gap: clamp(20px, 3.5vw, 64px);
  padding: 10px 2.6%;
  background: #ffffffed;
  border-bottom: 1px solid #eaf0fa;
  z-index: 10;
}
.brand {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
  text-decoration: none;
}
.brand-logo {
  display: block;
  width: 178px;
  height: 58px;
  flex-shrink: 0;
  object-fit: contain;
  object-position: left center;
}
.home-nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: clamp(20px, 2.5vw, 44px);
  white-space: nowrap;
}
.home-nav a {
  position: relative;
  padding: 14px 0;
  color: #253961;
  font-size: 15px;
  font-weight: 500;
  transition: color 160ms;
}
.home-nav .imathbook-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.home-nav a:hover, .home-nav .active {
  color: var(--blue);
}
.home-nav .active::after {
  content: '';
  position: absolute;
  height: 2px;
  bottom: 0;
  left: 0;
  right: 0;
  background: var(--blue);
}
.header-actions {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-left: auto;
  min-width: 0;
}
.course-search {
  display: flex;
  align-items: center;
  gap: 12px;
  border-radius: 28px;
  height: 44px;
  padding: 0 18px;
  background: #f0f5fc;
  color: #506899;
  width: clamp(190px, 23vw, 400px);
}
.course-search input {
  width: 100%;
  min-width: 0;
  outline: none;
  background: transparent;
  font-size: 13px;
  color: var(--ink);
}
.course-search input::placeholder {
  color: #8090b2;
}
.course-search:focus-within {
  box-shadow: 0 0 0 2px #a9c4ff;
}
.notification-button {
  display: grid;
  place-items: center;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: #f7faff;
  color: #25437b;
  flex-shrink: 0;
}
.login-link {
  white-space: nowrap;
  color: var(--blue);
}
.home-content {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
}
.home-hero {
  min-height: clamp(320px, 26vw, 420px);
  position: relative;
  isolation: isolate;
  overflow: hidden;
  background: #f4f8ff;
}
.hero-campus {
  position: absolute;
  inset: 0;
  z-index: -2;
  left: 35%;
  background-image: url('/mrt.jpg');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}
.home-hero::before {
  content: '';
  position: absolute;
  inset: 0;
  z-index: -1;
  background: linear-gradient(90deg, #f5f9ff 0%, #f5f9ff 35%, #f5f9ffed 42%, #f5f9ff88 51%, transparent 65%);
}
.hero-copy {
  padding: 36px 2.65% 20px;
}
.university-name {
  margin: 0 0 18px;
  color: #8296be;
  letter-spacing: 3.2px;
  font-size: 10px;
  font-weight: 600;
}
.hero-copy h1 {
  margin: 0 0 13px;
  font-size: clamp(42px, 3.4vw, 59px);
  line-height: 1.14;
  font-weight: 900;
  letter-spacing: -1.6px;
  color: #081330;
}
.hero-description {
  display: grid;
  gap: 3px;
  margin: 0 0 17px;
  font-size: 20px;
  line-height: 1.4;
  color: #7081aa;
  font-family: 'NotoSansSC', sans-serif;
}
.guide-button {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  gap: 14px;
  min-width: 156px;
  height: 46px;
  padding: 0 24px;
  font-size: 15px;
  font-weight: 600;
  border: 1px solid #e4ecf9;
  border-radius: 10px;
  background: #ffffffc9;
  box-shadow: 0 3px 12px #5874ae05;
}
.guide-button:hover {
  border-color: #abc5fc;
  background: #fff;
}
.course-section {
  position: relative;
  padding: 15px 2.5% 12px;
  border-radius: 18px 18px 0 0;
  background: linear-gradient(120deg, #fbfdff, #f7faff);
}
.section-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  gap: 16px;
}
.section-title {
  display: flex;
  align-items: center;
  gap: 19px;
}
.section-title h2 {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 0;
  font-size: 23px;
  font-weight: 750;
  white-space: nowrap;
}
.section-title h2::before {
  content: '';
  width: 6px;
  height: 23px;
  border-radius: 4px;
  background: var(--blue);
  box-shadow: 2px 0 6px #2f6eff20;
}
.section-title p {
  margin: 0;
  font-size: 14px;
  color: var(--muted);
}
.section-controls {
  display: flex;
  gap: 16px;
}
.section-controls button {
  display: grid;
  place-items: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #eff4fc;
  color: #1b3970;
}
.section-controls button:disabled {
  opacity: 0.45;
  cursor: default;
}
.course-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
}
.course-card {
  position: relative;
  min-width: 0;
  border: 1px solid #e6edf9;
  border-radius: 14px;
  background: linear-gradient(120deg, #fff, #fbfdff);
  box-shadow: 0 4px 14px #6688bd04;
  transition: border-color 180ms, box-shadow 180ms, transform 180ms;
}
.course-card:hover {
  transform: translateY(-3px);
  border-color: #b9cefa;
  box-shadow: 0 8px 20px #487bcb10;
}
.course-entry {
  position: relative;
  display: block;
  text-align: left;
  width: 100%;
  height: 184px;
  padding: 16px 23px;
  overflow: hidden;
  border-radius: inherit;
}
.course-number {
  display: block;
  color: #6f86b5;
  font-size: 14px;
  line-height: 20px;
  margin-bottom: 6px;
}
.course-icon {
  position: absolute;
  right: 22px;
  top: 10px;
  width: 36px;
  height: 34px;
  display: grid;
  place-items: center;
  color: #204887;
  background: #f1f5fc;
  border-radius: 50%;
}
.course-entry h3 {
  position: relative;
  z-index: 1;
  margin: 0 0 7px;
  font-size: clamp(18px, 1.35vw, 23px);
  font-weight: 750;
  line-height: 1.4;
  letter-spacing: -0.6px;
}
.course-entry p {
  position: relative;
  z-index: 1;
  width: 74%;
  min-height: 46px;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  margin: 0 0 11px;
  color: #7386af;
  font-size: 14px;
  line-height: 1.65;
  text-wrap: pretty;
}
.course-illustration {
  position: absolute;
  right: 12px;
  bottom: 10px;
  width: 43%;
  height: 125px;
  pointer-events: none;
  opacity: 0.8;
  mask-image: linear-gradient(90deg, transparent, #000 30%);
}
.course-arrow {
  position: relative;
  z-index: 1;
  width: 33px;
  height: 33px;
  display: grid;
  place-items: center;
  background: var(--blue);
  border: 3px solid #e9f0ff;
  border-radius: 50%;
  color: white;
  box-shadow: 0 2px 6px #1c61ff12;
}
.manage-course {
  position: absolute;
  top: 12px;
  right: 65px;
  color: #7183ad;
  padding: 5px;
}
.pagination {
  width: fit-content;
  max-width: 100%;
  flex-wrap: wrap;
  margin: 10px auto 0;
  display: flex;
  align-items: center;
  gap: 3px;
  padding: 3px 10px;
  border: 1px solid #e7eefb;
  border-radius: 24px;
  background: #ffffffc9;
  box-shadow: 0 3px 12px #487bcb05;
}
.pagination button {
  min-width: 36px;
  height: 35px;
  padding: 0 9px;
  font-size: 13px;
  color: #2d467a;
  border-radius: 50%;
}
.pagination button.selected {
  color: var(--blue);
  background: #eaf1ff;
  font-weight: 700;
}
.pagination button:disabled {
  color: #a5b4d0;
  cursor: default;
}
.pagination button:not(:disabled):hover {
  background: #edf3ff;
}
.other-courses {
  display: flex;
  justify-content: flex-end;
  margin-top: 8px;
  font-size: 12px;
  color: #758ab0;
}
.other-courses a {
  display: flex;
  align-items: center;
  gap: 6px;
}
.empty-courses {
  min-height: 280px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: var(--muted);
}
.empty-courses h3 {
  color: var(--ink);
  font-size: 20px;
}
.empty-courses button {
  color: var(--blue);
}
:where(a, button):focus-visible {
  outline: 2px solid var(--blue);
  outline-offset: 4px;
}
@media (min-width: 1800px) {
  .home-hero {
    min-height: 390px;
  }
  .course-entry {
    min-height: 205px;
  }
}
@media (max-width: 1300px) {
  .home-header {
    gap: 24px;
  }
  .brand {
    gap: 10px;
  }
  .home-nav {
    gap: 20px;
  }
  .home-nav a {
    font-size: 13px;
  }
  .header-actions {
    gap: 10px;
  }
  .course-search {
    width: 200px;
  }
  .course-entry {
    padding: 16px;
    min-height: 195px;
  }
  .course-entry p {
    font-size: 13px;
    width: 68%;
  }
  .course-illustration {
    opacity: 0.6;
    right: 5px;
  }
}
@media (max-width: 1050px) {
  .home-header {
    flex-wrap: wrap;
    gap: 6px 24px;
  }
  .header-actions {
    flex: 1;
    justify-content: flex-end;
  }
  .home-nav {
    order: 3;
    width: 100%;
    justify-content: space-around;
    overflow-x: auto;
  }
  .home-nav a {
    padding: 9px 0;
  }
  .course-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .course-entry h3 {
    font-size: 22px;
  }
  .course-entry p {
    font-size: 15px;
  }
  .home-hero {
    min-height: 350px;
  }
  .hero-campus {
    left: 20%;
  }
  .hero-copy {
    padding-left: 24px;
  }
  .home-hero::before {
    background: linear-gradient(90deg, #f5f9ff 0%, #f5f9fff0 40%, #f5f9ff55 75%);
  }
}
@media (max-width: 600px) {
  .home-header {
    padding: 12px 16px 4px;
    gap: 10px;
  }
  .brand-logo {
    width: 142px;
    height: 46px;
  }
  .header-actions {
    flex: 0;
    margin-left: auto;
  }
  .course-search {
    position: relative;
    width: 100%;
  }
  .home-header {
    padding-bottom: 10px;
  }
  .header-actions {
    flex-wrap: wrap;
  }
  .header-actions .course-search {
    order: 3;
    position: absolute;
    top: 120px;
    left: 16px;
    right: 16px;
    width: auto;
    height: 38px;
    background: #fff;
    border: 1px solid #e5edf9;
  }
  .home-nav {
    gap: 21px;
    justify-content: flex-start;
  }
  .home-nav a {
    font-size: 12px;
  }
  .notification-button {
    width: 30px;
  }
  .home-hero {
    min-height: 365px;
  }
  .hero-copy {
    padding: 67px 20px 24px;
  }
  .university-name {
    max-width: 300px;
    font-size: 8px;
    letter-spacing: 1.8px;
    line-height: 1.8;
    margin-bottom: 15px;
  }
  .hero-copy h1 {
    font-size: 39px;
  }
  .hero-description {
    font-size: 16px;
  }
  .hero-campus {
    left: 0;
    opacity: 0.65;
  }
  .home-hero::before {
    background: linear-gradient(90deg, #f5f9fff5 0%, #f5f9ffda 50%, #f5f9ff44);
  }
  .guide-button {
    height: 42px;
    min-width: 138px;
    font-size: 14px;
  }
  .course-section {
    padding: 18px 16px;
  }
  .section-title {
    display: block;
  }
  .section-title h2 {
    font-size: 21px;
  }
  .section-title p {
    font-size: 11px;
    margin-top: 7px;
  }
  .section-controls {
    gap: 7px;
  }
  .section-controls button {
    width: 30px;
    height: 30px;
  }
  .course-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }
  .course-entry {
    min-height: 184px;
    padding: 16px 20px;
  }
  .course-entry h3 {
    font-size: 21px;
  }
  .course-entry p {
    width: 64%;
    font-size: 14px;
  }
  .course-illustration {
    opacity: 0.8;
    right: 10px;
  }
}
@media (max-width: 600px) {
  .home-header {
    position: relative;
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 10px;
    padding: 14px 16px 12px;
  }
  .brand {
    grid-column: 1;
    grid-row: 1;
    min-height: 42px;
  }
  .home-nav {
    grid-column: 1 / -1;
    grid-row: 2;
    width: 100%;
  }
  .header-actions {
    display: contents;
  }
  .header-actions .course-search {
    position: static;
    grid-column: 1 / -1;
    grid-row: 3;
    width: 100%;
    height: 38px;
  }
  .notification-button {
    position: absolute;
    top: 20px;
    right: 68px;
  }
  .home-avatar, .login-link {
    position: absolute;
    top: 16px;
    right: 16px;
  }
  .hero-copy {
    padding-top: 30px;
  }
  .home-hero {
    min-height: 335px;
  }
}
@media (prefers-reduced-motion: reduce) {
  *, *::before {
    transition: none !important;
  }
  .course-card:hover {
    transform: none;
  }
}
</style>
